import { ReactElement, Suspense, useEffect } from 'react';
import {
  Button,
  TableColumns,
  TableContainer,
  TableRows,
  useTableContext,
  ContextualTablePagination,
  FullLoader,
  Pill,
  Modal,
  Input,
  ModalFooter,
} from '@sc/shared-ui';
import { LockClosedIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { useMutation, useSuspenseQuery } from '@apollo/client/react';
import {
  GQL_MUTATION_CREATE_PROJECT,
  GQL_QUERY_INDEX_PROJECTS,
} from '../utility/queries';
import {
  ProjectState,
  projectStateColourMap,
  projectStateDisplayMap,
  UserCompanyLimitation,
} from '@sc/graphql';
import Globe from '../assets/icons/globe-01.svg';
import Palette from '../assets/icons/palette.svg';
import { useForm } from '@tanstack/react-form';
import { useAlerts } from '../providers/alerts';
import { notify } from '../utility/notify';
import { useLocation, useRouter } from '@tanstack/react-router';
import { useUser } from '../providers/users';
import { track } from '../utility/analytics';

const ProjectTaleRows = ({ state }: { state?: ProjectState | undefined }) => {
  const { setTotalPages, page, rowsPerPage } = useTableContext();

  const { data } = useSuspenseQuery(GQL_QUERY_INDEX_PROJECTS, {
    variables: {
      input: {
        filter: {
          state,
        },
        pagination: {
          page,
          perPage: rowsPerPage,
        },
      },
    },
  });

  useEffect(() => {
    setTotalPages(data.indexProjects.pagination.lastPage);
  }, [data, setTotalPages]);

  return (
    <TableRows
      widthType="pc"
      rows={data.indexProjects.items.map((p) => ({
        uuid: p.uuid,
        cells: [
          {
            width: 22,
            content: p.name,
          },
          {
            width: 22,
            content: (
              <>
                <Globe className="size-5 mr-2 text-grey-400" />
                {p.domains.length > 0 ? (
                  <span className="text-sm">
                    {p.domains[0]?.url}
                    {p.domains.length > 1 && ` + ${p.domains.length - 1}`}
                  </span>
                ) : (
                  <span>-</span>
                )}
              </>
            ),
          },
          {
            width: 22,
            content: (
              <>
                <Palette className="size-5 mr-2 text-grey-400" />
                <span>{p.theme?.name ?? '-'}</span>
              </>
            ),
          },
          {
            width: 22,
            content: (
              <Pill
                size="small"
                colour={projectStateColourMap[p.state]}
                text={projectStateDisplayMap[p.state]}
              />
            ),
          },
          {
            width: 8,
            content: p.sourceCount,
          },
          {
            width: 16,
            content: p.dailyQueryCount,
          },
          {
            width: 10,
            content: (
              <Button
                button={{ text: 'View', type: 'secondary', size: 'medium' }}
                link={{
                  to: '/kb/$uuid',
                  params: {
                    uuid: p.uuid,
                  },
                }}
              />
            ),
          },
        ],
      }))}
    />
  );
};

const ProjectTable = ({ state }: { state?: ProjectState }) => (
  <TableContainer>
    <TableColumns
      widthType="pc"
      columns={[
        {
          width: 22,
          heading: 'name',
        },
        {
          width: 22,
          heading: 'Domain',
        },
        {
          width: 22,
          heading: 'Brand theme',
        },
        {
          width: 22,
          heading: 'State',
        },
        {
          width: 8,
          heading: 'Sources',
        },
        {
          width: 16,
          heading: 'Daily queries',
        },
        {
          width: 10,
        },
      ]}
    />

    <Suspense fallback={<FullLoader className="size-6" />}>
      <ProjectTaleRows state={state} />
    </Suspense>
    <ContextualTablePagination />
  </TableContainer>
);

export const Projects = (): ReactElement => {
  const [createProject, { loading }] = useMutation(GQL_MUTATION_CREATE_PROJECT);

  const { useApolloError } = useAlerts();

  const onError = useApolloError();

  const { hash } = useLocation();

  const { user } = useUser();

  const router = useRouter();

  useEffect(() => {
    if (hash === 'create') track('Create Project Modal Viewed');
  }, [hash]);

  const form = useForm({
    defaultValues: {
      name: '',
    },
    onSubmit: (form) =>
      void createProject({
        variables: {
          input: {
            name: form.value.name,
          },
        },
        refetchQueries: [GQL_QUERY_INDEX_PROJECTS],
        onCompleted: (data) => {
          track('Project Created', { projectUuid: data.createProject.uuid });

          notify.success('Successfully created knowledge base');

          void router.navigate({
            to: '/kb/$uuid',
            params: {
              uuid: data.createProject.uuid,
            },
            hash: 'sources-google-drive',
          });
        },
        onError,
      }),
  });

  const cannotCreateProject = user.company.limitations.includes(
    UserCompanyLimitation.cannotCreateProject
  );

  return (
    <>
      <div className="p-5 flex items-center justify-between">
        <h1 className="text-h1-small font-semibold font-grotesque">Knowledge bases</h1>
        <Button
          button={{
            text: 'Create knowledge base',
            size: 'medium',
            shrink: true,
            IconEnd: cannotCreateProject ? LockClosedIcon : PlusIcon,
            onClick: () =>
              void router.navigate({
                to: '/kb',
                hash: 'create',
              }),
            disabled: cannotCreateProject,
            tooltip: cannotCreateProject
              ? 'Maximum knowledge base cap hit'
              : undefined,
          }}
        />
      </div>
      <div className="pb-5 flex flex-col">
        <TabGroup className="flex flex-col">
          <TabList className="space-x-2 px-5">
            <Tab className="pt-3 px-3 pb-2.5 font-semibold data-selected:text-primary-400 hover:bg-grey-t-800 data-selected:border-primary-400 border-transparent border-b-2 outline-none">
              Active
            </Tab>
            <Tab className="pt-3 px-3 pb-2.5 font-semibold data-selected:text-primary-400 hover:bg-grey-t-800 data-selected:border-primary-400 border-transparent border-b-2 outline-none">
              Archived
            </Tab>
          </TabList>
          <TabPanels className="flex flex-col">
            <TabPanel>
              <ProjectTable />
            </TabPanel>
            <TabPanel>
              <ProjectTable state={ProjectState.archived} />
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </div>

      <Modal
        title="Create knowledge base"
        loading={loading}
        open={hash === 'create'}
        onClose={() =>
          void router.navigate({
            to: '/kb',
            hash: '',
          })
        }
        dialogPanelClassname="w-[440px]"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();

            void form.handleSubmit();
          }}
        >
          <div className="p-5 space-y-5">
            <form.Field
              name="name"
              children={({ state, handleChange }) => (
                <Input
                  label="Name"
                  value={state.value}
                  required
                  onChange={(e) => handleChange(e.target.value)}
                />
              )}
            />
          </div>
          <ModalFooter
            asForm
            buttonsDisabled={loading}
            onClose={() =>
              void router.navigate({
                to: '/kb',
                hash: '',
              })
            }
            submitText="Create"
          />
        </form>
      </Modal>
    </>
  );
};
